import type { LucideIcon } from "lucide-react";
import { ArrowDownRight, ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Minus } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { initials } from "@contractorhub/shared";
import type { ReactNode } from "react";
import { Button } from "./button";
import { cn } from "./cn";

/* -------------------------------------------------------------------------- */
/*  Avatar                                                                     */
/* -------------------------------------------------------------------------- */

const AVATAR_SIZE = {
  sm: "h-7 w-7 text-micro",
  md: "h-8 w-8 text-xs",
  lg: "h-10 w-10 text-sm",
} as const;

/**
 * The tint for a name.
 *
 * Written out as whole class strings, and living here rather than in
 * `packages/shared`, for the same reason `ALIGN` in `table.tsx` is a static map:
 * Tailwind finds classes by scanning source text. It never sees a name it has to
 * assemble — and it never scans `packages/shared` at all, which resolves through
 * the `node_modules` symlink that v4's auto-detection skips. This function used
 * to live there and name a `brand-*` ramp that does not exist, so every avatar in
 * the product rendered as bare initials on no background.
 */
const AVATAR_TINT = [
  "bg-avatar-1 text-avatar-1-on",
  "bg-avatar-2 text-avatar-2-on",
  "bg-avatar-3 text-avatar-3-on",
  "bg-avatar-4 text-avatar-4-on",
  "bg-avatar-5 text-avatar-5-on",
  "bg-avatar-6 text-avatar-6-on",
] as const;

function avatarColor(seed: string): string {
  // `charCodeAt` on "" is NaN, and NaN % 6 is NaN — which indexed to `undefined`
  // and rendered an untinted circle for anyone with an empty name.
  const code = seed.charCodeAt(0);
  const index = Number.isNaN(code) ? 0 : code % AVATAR_TINT.length;
  return AVATAR_TINT[index] ?? AVATAR_TINT[0];
}

export function Avatar({
  name,
  size = "md",
  className = "",
}: {
  name: string;
  size?: keyof typeof AVATAR_SIZE;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid shrink-0 place-items-center rounded-full font-semibold",
        AVATAR_SIZE[size],
        avatarColor(name),
        className
      )}
      aria-hidden="true"
    >
      {initials(name)}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Card                                                                       */
/* -------------------------------------------------------------------------- */

export function Card({
  children,
  className = "",
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "section" | "article";
}) {
  return (
    <Tag
      className={cn(
        "rounded-xl border border-line bg-surface",
        className
      )}
    >
      {children}
    </Tag>
  );
}

export function CardHeader({
  title,
  description,
  icon: Icon,
  actions,
  className = "",
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-4 border-b border-line px-5 py-4",
        className
      )}
    >
      <div className="flex min-w-0 items-start gap-2.5">
        {Icon && (
          <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent-on-soft">
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          </div>
        )}
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-text">{title}</h2>
          {description && (
            <p className="mt-0.5 text-sm text-subtle">{description}</p>
          )}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page header                                                                */
/* -------------------------------------------------------------------------- */

/**
 * One page header.
 *
 * There were three different shapes across eight admin pages — `items-end` here,
 * `items-start` there, a bare `mb-6` on the rest — and subtitles that appeared on
 * six pages and were simply missing on two. Navigating between them made the
 * title visibly shift.
 */
export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
  className = "",
}: {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mb-6 flex flex-wrap items-end justify-between gap-x-4 gap-y-3",
        className
      )}
    >
      <div className="min-w-0">
        {eyebrow && (
          <div className="mb-1 text-micro font-medium uppercase tracking-wider text-subtle">
            {eyebrow}
          </div>
        )}
        <h1 className="text-xl font-semibold text-text">{title}</h1>
        {description && (
          <p className="mt-1 text-sm text-subtle">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Stat card                                                                  */
/* -------------------------------------------------------------------------- */

const STAT_TONE = {
  neutral: "bg-surface-sunken text-muted",
  accent: "bg-accent-soft text-accent-on-soft",
  success: "bg-success-soft text-success-on-soft",
  warning: "bg-warning-soft text-warning-on-soft",
  danger: "bg-danger-soft text-danger-on-soft",
} as const;

export function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  tone = "accent",
  to,
  search,
}: {
  label: string;
  value: ReactNode;
  icon: LucideIcon;
  hint?: string;
  tone?: keyof typeof STAT_TONE;
  to?: string;
  search?: Record<string, unknown>;
}) {
  const base =
    "rounded-xl border border-line bg-surface p-4 flex items-start justify-between gap-3";

  const inner = (
    <>
      <div className="min-w-0">
        <div className="text-xs font-medium text-muted">{label}</div>
        <div className="mt-1 text-kpi font-semibold text-text">{value}</div>
        {hint && <div className="mt-0.5 text-xs text-subtle">{hint}</div>}
      </div>
      <div
        className={cn(
          "grid h-9 w-9 shrink-0 place-items-center rounded-lg",
          STAT_TONE[tone]
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </div>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        search={search}
        className={cn(
          base,
          "transition-colors hover:border-accent-soft-border hover:bg-surface-hover"
        )}
      >
        {inner}
      </Link>
    );
  }

  return <div className={base}>{inner}</div>;
}

/* -------------------------------------------------------------------------- */
/*  Panel                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A titled section of a dashboard: `Card` and `CardHeader` with an optional
 * "View all" link and a padded body. Every dashboard section is one of these,
 * so a dashboard has no card dialect of its own.
 */
export function Panel({
  title,
  description,
  actions,
  viewAll,
  children,
  className = "",
  bodyClassName = "p-5",
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  viewAll?: { to: string; search?: Record<string, unknown>; label?: string };
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <Card as="section" className={cn("flex min-w-0 flex-col", className)}>
      <CardHeader
        title={title}
        description={description}
        actions={
          actions || viewAll ? (
            <>
              {actions}
              {viewAll && (
                <Link
                  to={viewAll.to}
                  search={viewAll.search}
                  className="inline-flex items-center gap-1 rounded-md text-xs font-medium text-accent hover:text-accent-hover"
                >
                  {viewAll.label ?? "View all"}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              )}
            </>
          ) : undefined
        }
      />
      <div className={cn("min-w-0 flex-1", bodyClassName)}>{children}</div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  KPI card                                                                   */
/* -------------------------------------------------------------------------- */

export type KpiDelta = {
  /** Signed change, as a fraction: 0.084 is +8.4%. */
  value: number;
  /** What it is compared with: "vs last month". */
  label: string;
  /** Whether a rise is good news (hours) or bad (a backlog). */
  goodWhen: "up" | "down";
};

/**
 * One headline number: a sentence-case label, the value, and optionally the
 * change against a named period and a sparkline.
 *
 * The delta is an arrow and signed text, coloured by whether the move is good,
 * so it never rests on colour alone. `trend` is a slot rather than data so this
 * kit stays free of the chart library (see `components/charts/index.ts`).
 */
export function KpiCard({
  label,
  value,
  hint,
  delta,
  trend,
  to,
  search,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  delta?: KpiDelta | null;
  trend?: ReactNode;
  to?: string;
  search?: Record<string, unknown>;
}) {
  const base = "flex min-w-0 flex-col rounded-xl border border-line bg-surface p-4";
  const inner = (
    <>
      <div className="text-xs font-medium text-muted">{label}</div>
      <div className="mt-1.5 flex items-end justify-between gap-3">
        <div className="min-w-0 truncate text-kpi font-semibold text-text">{value}</div>
        {trend && <div className="mb-1 w-20 shrink-0">{trend}</div>}
      </div>
      <div className="mt-1.5 flex min-h-4 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
        {delta && <DeltaBadge delta={delta} />}
        {hint && <span className="min-w-0 truncate text-subtle">{hint}</span>}
      </div>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        search={search}
        className={cn(base, "transition-colors hover:border-line-strong hover:bg-surface-hover")}
      >
        {inner}
      </Link>
    );
  }
  return <div className={base}>{inner}</div>;
}

const percent = new Intl.NumberFormat("en-AU", {
  style: "percent",
  maximumFractionDigits: 1,
  signDisplay: "exceptZero",
});

function DeltaBadge({ delta }: { delta: KpiDelta }) {
  // Under a tenth of a percent is "no change", not a coloured +0.0%.
  const flat = Math.abs(delta.value) < 0.001;
  const up = delta.value > 0;
  const good = flat ? null : up === (delta.goodWhen === "up");
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 font-medium",
        good === null ? "text-muted" : good ? "text-success-on-soft" : "text-danger-on-soft"
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {flat ? "No change" : percent.format(delta.value)}
      <span className="font-normal text-subtle">{delta.label}</span>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Tabs                                                                       */
/* -------------------------------------------------------------------------- */

export interface TabItem<T extends string> {
  value: T;
  label: string;
  count?: number;
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  className = "",
}: {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (next: T) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        "flex items-center gap-1 overflow-x-auto border-b border-line scrollbar-thin",
        className
      )}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={cn(
              "-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-sm transition-colors",
              active
                ? "border-accent font-semibold text-accent"
                : "border-transparent text-subtle hover:text-text"
            )}
          >
            {item.label}
            {item.count !== undefined && (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-micro font-medium",
                  active
                    ? "bg-accent-soft text-accent-on-soft"
                    : "bg-surface-sunken text-subtle"
                )}
              >
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Pagination                                                                 */
/* -------------------------------------------------------------------------- */

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  className = "",
}: {
  /** 1-based. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (next: number) => void;
  className?: string;
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3",
        className
      )}
    >
      <p className="text-xs text-subtle">
        Showing <span className="font-medium text-text">{first}</span>–
        <span className="font-medium text-text">{last}</span> of{" "}
        <span className="font-medium text-text">{total}</span>
      </p>
      <div className="flex items-center gap-1.5">
        <Button
          variant="secondary"
          size="sm"
          icon={ChevronLeft}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <span className="px-1 text-xs text-subtle">
          {page} / {pageCount}
        </span>
        <Button
          variant="secondary"
          size="sm"
          trailingIcon={ChevronRight}
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
